import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Check, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTranslations } from '../context/TranslationsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { td: tCms } = useTranslations(['auth', 'common', 'errors']);
  const lang = i18n.language;
  const token = searchParams.get('token');
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    return (cmsValue && cmsValue !== key) ? cmsValue : fallback;
  };

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Password validation
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isPasswordStrong = hasMinLength && hasUppercase && hasNumber;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordStrong) {
      setError(t('auth.passwordNotMeetRequirements', 'Password does not meet requirements'));
      return;
    }
    
    if (!passwordsMatch) {
      setError(t('auth.passwordsDontMatch', 'Passwords do not match'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        password
      });
      
      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.errorPl || err.response?.data?.error || 
        t('auth.failedToReset', 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <div className="glass-card p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">
            {t('auth.invalidLink', 'Invalid link')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('auth.invalidLinkDescription', 'The password reset link is invalid or has expired.')}
          </p>
          <Link to="/login" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-5 h-5" />
            {t('auth.backToLogin', 'Back to login')}
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-green-500 mb-4">
            {t('auth.passwordChanged', 'Password changed!')}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t('auth.passwordChangedDescription', 'Your password has been successfully changed. You can now log in.')}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary w-full"
          >
            {t('auth.goToLogin', 'Go to login')}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8 max-w-md w-full"
      >
        <h1 className="text-2xl font-bold text-center mb-2">
          {t('auth.setNewPassword', 'Set new password')}
        </h1>
        <p className="text-muted-foreground text-center mb-8">
          {t('auth.setNewPasswordDescription', 'Enter your new password below')}
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-500 p-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label>{t('auth.newPassword', 'New password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Password requirements */}
          <div className="space-y-1 text-sm">
            <div className={`flex items-center gap-2 ${hasMinLength ? 'text-green-500' : 'text-muted-foreground'}`}>
              <Check className={`w-4 h-4 ${hasMinLength ? 'opacity-100' : 'opacity-30'}`} />
              {t('auth.min8chars', 'At least 8 characters')}
            </div>
            <div className={`flex items-center gap-2 ${hasUppercase ? 'text-green-500' : 'text-muted-foreground'}`}>
              <Check className={`w-4 h-4 ${hasUppercase ? 'opacity-100' : 'opacity-30'}`} />
              {t('auth.uppercaseLetter', 'Uppercase letter')}
            </div>
            <div className={`flex items-center gap-2 ${hasNumber ? 'text-green-500' : 'text-muted-foreground'}`}>
              <Check className={`w-4 h-4 ${hasNumber ? 'opacity-100' : 'opacity-30'}`} />
              {t('auth.aNumber', 'Number')}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('auth.confirmPasswordLabel', 'Confirm password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10"
                placeholder="••••••••"
              />
            </div>
            {confirmPassword && (
              <p className={`text-sm ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}>
                {passwordsMatch 
                  ? `✓ ${t('auth.passwordsMatch', 'Passwords match')}`
                  : `✗ ${t('auth.passwordsNotMatch', 'Passwords do not match')}`}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !isPasswordStrong || !passwordsMatch}
            className="btn-primary w-full disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('auth.saving', 'Saving...')}
              </div>
            ) : (
              t('auth.saveNewPassword', 'Save new password')
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-primary hover:underline text-sm">
            {t('auth.backToLogin', 'Back to login')}
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;
